<template>
  <v-dialog
    v-model="isVisible"
    max-width="600px"
  >
    <v-card>
      <material-card
        :title="title"
        tile
        color="green"
        full-width
      >
        <v-container>
          <v-layout wrap>
            <!-- Message Body -->
            <v-flex
              xs12
              md12
            >
              <span>{{ message }}</span>
            </v-flex>
            <!-- Input Area for MATCH_TEXT and REQUIRE_INPUT mode -->
            <v-flex
              v-if="mode === MODAL_MODE.MATCH_TEXT || mode === MODAL_MODE.REQUIRE_INPUT"
              xs12
              md12
              class="mt-3"
            >
              <v-textarea
                v-if="inputLines > 1 && inputType !== 'password'"
                v-model="textInput"
                :label="inputTitle"
                :rows="inputLines"
                :prepend-icon="prependIcon"
              />
              <v-text-field
                v-else
                v-model="textInput"
                :label="inputTitle"
                :prepend-icon="prependIcon"
                :append-icon="isPasswordType ? (showPassword ? 'visibility' : 'visibility_off') : null"
                :type="isPasswordType ? (showPassword ? 'text' : 'password') : 'text'"
                class="success-input required"
                hide-details
                @click:append="togglePassword"
              />
              <!-- Display a hint text to guide the user on what to type -->
              <div
                  v-if="mode === MODAL_MODE.MATCH_TEXT"
                  class="caption grey--text mt-1"
                  :style="prependIcon ? 'padding-left: 32px;' : ''"
                  v-html="$t('common.matchTextHint', { text: matchText })"
                >
              </div>
            </v-flex>
          </v-layout>
        </v-container>
      </material-card>
      <v-card-actions>
        <v-spacer />
        <!-- Close/Cancel Button -->
        <v-btn
          v-if="showCancelButton"
          small
          color="error darken-1"
          @click="hideModal"
        >
          {{ closeText }}
        </v-btn>
        <!-- Confirm/OK Button -->
        <v-btn
          small
          color="success darken-1"
          :disabled="!isConfirmEnabled"
          @click="pressConfirm($event)"
        >
          {{ confirmText }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { ModalEventBus } from './messagemodalplugin.js';
import { MODAL_MODE } from './modalMode';

export default {
  data() {
    return {
      MODAL_MODE,
      isVisible: false,

      // Common modal data
      title: '',
      message: '',
      confirmText: '',
      closeText: '',
      mode: MODAL_MODE.NORMAL,
      hasCancel: true,

      // Data for inputs (MATCH_TEXT and REQUIRE_INPUT mode)
      inputTitle: '',
      prependIcon: null,
      inputLines: 1,
      matchText: 'DELETE',
      inputType: 'text',

      // Internal states
      textInput: '',
      showPassword: false,
      onConfirmCallback: null
    };
  },
  computed: {
    /**
     * Check if the current input should be treated as a password field
     */
     isPasswordType() {
      return this.mode === MODAL_MODE.REQUIRE_INPUT && this.inputType === 'password';
    },
    /**
     * Check if Cancel is visible
     */
    showCancelButton() {
      // Only check hide Cancel button in NORMAL mode
      if (this.mode === MODAL_MODE.NORMAL) {
        return this.hasCancel;
      }
      return true;
    },
    /**
     * Validate if the confirm button should be enabled based on the current mode
     */
    isConfirmEnabled() {
      switch (this.mode) {
        case MODAL_MODE.INFO_ONLY:
        case MODAL_MODE.CONFIRM_CANCEL:
          return true;
        case MODAL_MODE.MATCH_TEXT:
          return this.textInput === this.matchText;
        case MODAL_MODE.REQUIRE_INPUT:
          return !!this.textInput && this.textInput.trim().length > 0;
        default:
          return true;
      }
    }
  },
  beforeMount() {
    // Listen for the global event from the Plugin
    ModalEventBus.$on('showModal', (params) => {
      this.showModal(params);
    });
  },
  beforeDestroy() {
    // Prevent memory leaks when the component is destroyed
    ModalEventBus.$off('showModal');
  },
  methods: {
    /**
     * Handle confirm click
     */
    pressConfirm(event) {
      event.preventDefault();
      this.isVisible = false;

      // Execute the callback function passed from the parent if it exists
      if (typeof this.onConfirmCallback === 'function') {
        // We pass this.textInput to the callback so the caller can read the typed data
        this.onConfirmCallback(this.textInput);
      }
    },
    /**
     * Process the modal configuration when called via Plugin
     */
    showModal(params) {
      // Reset internal states
      this.textInput = '';
      this.showPassword = false;
      this.isVisible = true;

      // Assign core data (Title and Text fallbacks based on i18n)
      this.title = params.title || this.$t('common.notification');
      this.message = params.message || '';
      this.confirmText = params.confirmText || this.$t('common.confirm');
      this.closeText = params.closeText || this.$t('common.close');

      // Assign logic mode and configurations, fallback to defaults
      this.mode = params.mode || MODAL_MODE.NORMAL;
      this.hasCancel = params.hasCancel !== undefined ? params.hasCancel : true;
      this.inputTitle = params.inputTitle || '';
      this.prependIcon = params.prependIcon || null;
      this.inputLines = params.inputLines || 1;
      this.matchText = params.matchText || 'DELETE';
      this.inputType = params.inputType || 'text';

      // Assign callback function
      this.onConfirmCallback = params.onConfirm;
    },
    /**
     * Close the modal
     */
    hideModal(event) {
      if (event) event.preventDefault();
      this.isVisible = false;
    },
    /**
     * Toggle password visibility
     */
    togglePassword() {
      this.showPassword = !this.showPassword;
    }
  }
}
</script>

<style lang="scss" scoped>
.modal {
  box-shadow: 2px 2px 20px 1px;
  overflow-x: auto;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
}
.v-card {
  margin-bottom: 0px !important;
  margin-top: 21px !important;
}
</style>
